<template>
  <button ref="button" :clear="clear"
    :disabled="disabled"
    :class="{ clear: clear }"
    :style="`flex-direction: ${iconLocation === 'right' ? 'row-reverse' : 'row'}`"
  >
    <span v-if="icon" :class="[icon, 'button-icon']"></span>
    <slot></slot>
  </button>
</template>

<script setup lang="ts">
import { ref, toRefs } from "vue";

interface IMcButtonProps {
  icon?: string;
  iconLocation?: "left" | "right" | "center";
  clear?: boolean;
  disabled?: boolean | undefined;
}

const props = withDefaults(defineProps<IMcButtonProps>(), {
  icon: "",
  iconLocation: "left",
  clear: false,
  disabled: false
});

const {
  icon,
  iconLocation,
  clear,
  disabled
} = toRefs(props);

const button = ref<HTMLButtonElement | null>(null);

const blur = () => button.value?.blur();

defineExpose({
  blur
});
</script>

<style>
:root, * {
  /* Button default styles */
  --button-height: 32px;
  --button-width: auto;
  --button-min-width: unset;
  --button-max-width: unset;
  --button-padding: 12px;
  --button-gap: 8px;
  --button-font-size: var(--font-size-body-regular);
  --button-font-weight: var(--font-weight-body-medium);
  --button-line-height: var(--line-height-m);
  --button-text-color: var(--colour-text-contrast);
  --button-icon-color: unset;
  --button-icon-font-size: unset;
  --button-icon-font-weight: unset;
  --button-icon-margin: unset;
  --button-background-color: var(--colour-background-neutral-default);
  --button-border-color: var(--colour-border-neutral-default);
  --button-border: solid 1px var(--button-border-color);
  --button-border-radius: 3px;
  --button-box-shadow: none;
  --button-outline: none;
  --button-outline-offset: 2px;
  --button-justify-content: center;
  /* Button hover styles */
  --button-text-color-hover: var(--button-text-color);
  --button-icon-color-hover: var(--button-icon-color);
  --button-icon-font-weight-hover: var(--button-icon-font-weight);
  --button-background-color-hover: var(--colour-background-neutral-hover);
  --button-border-color-hover: var(--button-border-color);
  --button-border-hover: solid 1px var(--button-border-color-hover);
  --button-box-shadow-hover: none;
  --button-outline-hover: none;
  /* Button active styles */
  --button-text-color-active: var(--button-text-color-hover);
  --button-icon-color-active: var(--button-icon-color-hover);
  --button-icon-font-weight-active: var(--button-icon-font-weight-hover);
  --button-background-color-active: color-mix(in srgb, var(--button-background-color, var(--primary-color)) 70%, var(--colour-text-contrast));
  --button-border-color-active: var(--button-border-color-hover);
  --button-border-active: solid 1px var(--button-border-color-active);
  --button-outline-active: none;
  --button-box-shadow-active: none;
  /* Button focus styles */
  --button-text-color-focus: var(--button-text-color-hover);
  --button-icon-color-focus: var(--button-icon-color-hover);
  --button-icon-font-weight-focus: var(--button-icon-font-weight-hover);
  --button-background-color-focus: var(--button-background-color-hover);
  --button-border-color-focus: var(--button-border-color-hover);
  --button-border-focus: solid 1px var(--button-border-color-focus);
  --button-outline-focus: 2px solid var(--colour-border-brand-default);
  --button-box-shadow-focus: none;
  /* Button disabled styles */
  --button-text-color-disabled: var(--colour-text-disabled);
  --button-icon-color-disabled: var(--colour-icons-disabled);
  --button-background-color-disabled: var(--colour-background-disabled);
  --button-border-color-disabled: var(--colour-border-neutral-default);
  --button-border-disabled: none;
  --button-outline-disabled: none;
}
</style>

<style scoped lang="scss">
button {
  display: inline-flex;
  text-align: center;
  height: var(--button-height);
  width: var(--button-width);
  min-width: var(--button-min-width);
  max-width: var(--button-max-width);
  justify-content: var(--button-justify-content);
  padding: var(--button-padding);
  align-items: center;
  flex-shrink: 0;
  border: var(--button-border);
  border-radius: var(--button-border-radius);
  background-color: var(--button-background-color);
  color: var(--button-text-color);
  font-size: var(--button-font-size);
  font-style: normal;
  font-weight: var(--button-font-weight);
  line-height: var(--button-line-height);
  gap: var(--button-gap);
  transition: all 0.2s ease-in-out;
  user-select: none;
  outline: var(--button-outline);
  outline-offset: var(--button-outline-offset);
  box-shadow: var(--button-box-shadow);

  .button-icon {
    color: var(--button-icon-color);
    font-size: var(--button-icon-font-size);
    font-weight: var(--button-icon-font-weight);
    margin: var(--button-icon-margin);
  }

  &:hover {
    color: var(--button-text-color-hover);
    background: var(--button-background-color-hover);
    border: var(--button-border-hover);
    box-shadow: var(--button-box-shadow-hover);
    outline: var(--button-outline-hover);

    .button-icon {
      color: var(--button-icon-color-hover);
      font-weight: var(--button-icon-font-weight-hover);
    }
  }

  &.active,
  &:active {
    color: var(--button-text-color-active);
    background: var(--button-background-color-active);
    border: var(--button-border-active);
    box-shadow: var(--button-box-shadow-active);
    outline: var(--button-outline-active);

    .button-icon {
      color: var(--button-icon-color-active);
      font-weight: var(--button-icon-font-weight-active);
    }
  }

  &:focus:not(:disabled):not(:active):not(.active) {
    color: var(--button-text-color-focus);
    background: var(--button-background-color-focus);
    border: var(--button-border-focus);
    box-shadow: var(--button-box-shadow-focus);
    outline: var(--button-outline-focus);

    .button-icon {
      color: var(--button-icon-color-focus);
      font-weight: var(--button-icon-font-weight-focus);
    }
  }

  &:disabled {
    color: var(--button-text-color-disabled);
    background: var(--button-background-color-disabled);
    border-color: var(--button-border-color-disabled);
    border: var(--button-border-disabled);
    box-shadow: none;
    cursor: not-allowed;

    .button-icon {
      color: var(--button-icon-color-disabled);
    }
  }
}

button.clear {
  background: transparent;
  border: none;
  box-shadow: none;

  &:hover,
  &:active,
  &:focus {
    border: none;
    box-shadow: none;
  }

  &:hover {
    color: var(--button-text-color);
    background: var(--button-background-color-hover);
  }

  &.active,
  &:active {
    color: var(--button-text-color-active);
    background: var(--button-background-color-active);

    .button-icon {
      color: var(--button-icon-color-active);
      font-weight: var(--button-icon-font-weight-active);
    }
  }

  &:focus:not(:disabled):not(:active):not(.active) {
    background: var(--button-background-color-focus);
  }

  &:disabled {
    color: var(--button-text-color-disabled);
    background: var(--button-background-color-disabled);
    cursor: not-allowed;

    .button-icon {
      color: var(--button-icon-color-disabled);
    }
  }
}

// slotted icon
button::slotted(.icon) {
  margin-right: 8px;
  margin-left: 8px;
}
</style>
